import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { parentAPI, gmailAPI } from '../../lib/api';
import {
  AlertCircle, RefreshCw, Link2, Check, Archive, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Parent Today — Guided childcare daily view.
 *
 * Structure:
 * 1. Status Sentence — one line, changes through the day
 * 2. Day Timeline — chronological, caregiver as character, activity as anchor
 * 3. Tasks — conditional, bottom
 *
 * Principle: "This didn't just happen. It was guided."
 */
export const ParentToday = () => {
  const [searchParams] = useSearchParams();
  const urlFamilyId = searchParams.get('family_id');
  const familyId = (urlFamilyId && urlFamilyId !== 'null')
    ? urlFamilyId
    : localStorage.getItem('family_id');

  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [dailyPlan, setDailyPlan] = useState(null);
  const [caregiverName, setCaregiverName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectingGmail, setConnectingGmail] = useState(false);
  const [completingTasks, setCompletingTasks] = useState(new Set());

  const STALE_DAYS = 14;
  const isTaskStale = (task) => {
    const now = new Date();
    const ref = task.due_date ? new Date(task.due_date) : task.created_at ? new Date(task.created_at) : null;
    return ref && (now - ref) / 86400000 > STALE_DAYS;
  };

  const handleTaskAction = async (taskId, action) => {
    setCompletingTasks(prev => new Set([...prev, taskId]));
    try {
      const status = action === 'done' ? 'done' : 'cancelled';
      await parentAPI.updateTaskStatus(taskId, status);
      setData(prev => ({
        ...prev,
        tasks: {
          ...prev.tasks,
          due_today: prev.tasks.due_today.filter(t => t.id !== taskId),
          pending_count: Math.max(0, (prev.tasks.pending_count || 1) - 1)
        }
      }));
      if (action === 'done') toast.success('Task marked as done!');
      else toast.info('Task archived');
    } catch {
      toast.error('Could not update task. Please try again.');
    } finally {
      setCompletingTasks(prev => { const n = new Set(prev); n.delete(taskId); return n; });
    }
  };

  useEffect(() => {
    if (familyId) loadAllData();
  }, [familyId]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [todayRes, timelineRes, planRes, settingsRes] = await Promise.allSettled([
        parentAPI.getToday(familyId),
        parentAPI.getTimeline(familyId),
        parentAPI.getDailyPlan(familyId),
        parentAPI.getSettings(familyId),
      ]);

      if (todayRes.status === 'fulfilled' && todayRes.value.data?.ok) {
        setData(todayRes.value.data);
      } else {
        setError('Failed to load dashboard');
        return;
      }

      if (timelineRes.status === 'fulfilled' && timelineRes.value.data?.ok) {
        setTimeline(timelineRes.value.data);
      }

      if (planRes.status === 'fulfilled' && planRes.value.data?.ok) {
        setDailyPlan(planRes.value.data.plan);
      }

      if (settingsRes.status === 'fulfilled' && settingsRes.value.data?.ok) {
        const caregivers = settingsRes.value.data.caregivers || [];
        if (caregivers.length > 0) {
          const name = caregivers[0].full_name;
          setCaregiverName(name ? name.split(' ')[0] : null);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      setConnectingGmail(true);
      const response = await gmailAPI.startAuth(familyId);
      if (response.data?.ok && response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data?.error || 'Failed to start Gmail connection');
      }
    } catch {
      toast.error('Failed to connect Gmail');
    } finally {
      setConnectingGmail(false);
    }
  };

  // ── Render guards ──

  if (!familyId) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">No Family Selected</h2>
        <Link to="/login" className="text-primary hover:underline">Please log in</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="today-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-slate-400">Loading today's update...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-100 text-red-700 px-6 py-4 rounded-2xl flex items-center justify-between">
        <span>{error}</span>
        <button onClick={loadAllData}
          className="ml-4 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-xl text-sm font-medium transition-colors flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Retry
        </button>
      </div>
    );
  }

  const {
    family, children, gmail, tasks, recent_inbox,
    nanny, child_status, child_statuses, today_routines, upcoming_events
  } = data || {};

  const primaryChild = children?.[0];
  const childName = children?.length > 1
    ? children.map(c => c.first_name).join(' & ')
    : primaryChild?.first_name || family?.family_name || 'Your child';

  const cName = caregiverName || 'Your caregiver';

  const todayLabel = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long'
  });

  const statusSentence = buildStatusSentence(childName, cName, child_status, nanny, dailyPlan, timeline);
  const timelineEntries = buildTimelineEntries(timeline, dailyPlan, nanny, recent_inbox, childName, cName);
  const planItems = buildPlanItems(tasks, today_routines, primaryChild);
  const freshPhotos = (nanny?.photos_today || []).filter(p => p?.url || p?.id);
  const taskCount = tasks?.due_today?.length || 0;

  return (
    <div data-testid="parent-today-page" className="max-w-xl mx-auto pb-10">

      {/* ── Header ── */}
      <div className="pt-2 pb-2 md:pt-0">
        <div className="flex items-start justify-between">
          <p className="text-sm text-slate-400">{todayLabel}</p>
          {!gmail?.connected && (
            <button onClick={handleConnectGmail} disabled={connectingGmail}
              className="flex items-center gap-1.5 px-2.5 py-1 text-slate-400 hover:text-primary text-xs transition-colors">
              {connectingGmail ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
              Connect Gmail
            </button>
          )}
        </div>
      </div>

      {/* ── 1. Status Sentence ── */}
      <div className="py-6" data-testid="status-sentence">
        <div className="flex items-start gap-3">
          <div className={`w-2.5 h-2.5 rounded-full mt-2 flex-shrink-0 ${
            statusSentence.dot === 'red' ? 'bg-red-500' :
            statusSentence.dot === 'amber' ? 'bg-amber-500' :
            'bg-emerald-500'
          }`} />
          <h1 className="text-xl text-slate-800 leading-relaxed">
            {statusSentence.text}
          </h1>
        </div>
      </div>

      {/* ── 2. Day Timeline ── */}
      {timelineEntries.length > 0 ? (
        <div className="mb-8" data-testid="day-timeline">
          <div className="relative pl-6">
            <div className="absolute left-[4px] top-2 bottom-2 w-px bg-slate-100" />
            <div className="space-y-6">
              {timelineEntries.map((entry, idx) => (
                <TimelineEntryComponent
                  key={entry.id || idx}
                  entry={entry}
                  photos={freshPhotos}
                  planItems={entry.type === 'morning_plan' ? planItems : null}
                  childName={childName}
                  cName={cName}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <MorningPreview
          planItems={planItems}
          cName={cName}
          childStatus={child_status}
          primaryChild={primaryChild}
        />
      )}

      {/* ── 3. Tasks ── */}
      {taskCount > 0 && (
        <div className="mb-6" data-testid="tasks-card">
          <div className="px-1 mb-3">
            <p className="text-sm text-slate-400">
              {taskCount === 1 ? '1 task needs attention' : `${taskCount} tasks need attention`}
            </p>
          </div>
          <div className="space-y-2">
            {tasks.due_today.slice(0, 5).map((task) => {
              const stale = isTaskStale(task);
              const busy = completingTasks.has(task.id);
              const todayStr = new Date().toISOString().split('T')[0];
              const overdue = task.due_date && task.due_date < todayStr;
              return (
                <div key={task.id}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    stale ? 'bg-amber-50/60 border border-amber-100' : 'bg-white border border-slate-100'
                  }`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{task.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {overdue ? 'Overdue' : 'Due today'}
                      {stale && <span className="ml-1.5 text-amber-600">· May be outdated</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button disabled={busy} onClick={() => handleTaskAction(task.id, 'done')}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary rounded-lg text-xs font-medium transition-colors disabled:opacity-40">
                      <Check className="w-3 h-3" /> Done
                    </button>
                    <button disabled={busy} onClick={() => handleTaskAction(task.id, 'archive')}
                      className="p-1.5 hover:bg-slate-100 text-slate-400 rounded-lg transition-colors disabled:opacity-40"
                      title="Archive">
                      <Archive className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
            {tasks.due_today.length > 5 && (
              <Link to={`/parent/week?family_id=${familyId}`}
                className="block text-xs text-primary text-center pt-1 hover:underline">
                +{tasks.due_today.length - 5} more — see all tasks
              </Link>
            )}
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div className="flex items-center justify-center pt-2 pb-4">
        <Link to={`/parent/updates?family_id=${familyId}`}
          className="text-xs text-slate-400 hover:text-primary flex items-center gap-1 transition-colors">
          All updates <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};


// ─── Status Sentence ────────────────────────────────────────────────

function buildStatusSentence(childName, cName, childStatus, nanny, dailyPlan, timeline) {
  const state = childStatus?.state;
  const report = nanny?.report;
  const activity = (dailyPlan?.activity_checklist || []).find(a => a.status === 'done');
  const events = timeline?.events || [];
  const hour = new Date().getHours();

  const hasHealthAlert = events.some(e => e.urgent || (e.source || '').includes('urgent'));
  const hasMissedCheckin = events.some(e => (e.source || '').includes('missed'));

  if (hasHealthAlert) {
    return { text: `Health concern flagged for ${childName}.`, dot: 'red' };
  }
  if (hasMissedCheckin && state === 'pickup_pending') {
    return { text: `${cName} hasn't responded to the pickup check-in.`, dot: 'amber' };
  }
  if (state === 'no_school') {
    return { text: `${childName} is home today.`, dot: 'green' };
  }
  if (activity && report?.mood && hour >= 18) {
    return {
      text: `${childName}'s day is complete. ${activity.activity_title}, ${report.mood} mood throughout.`,
      dot: 'green'
    };
  }
  if (activity && state === 'picked_up') {
    const lunch = report?.lunch
      ? `, ${report.lunch.toLowerCase().includes('full') ? 'full' : report.lunch.toLowerCase()} lunch`
      : '';
    return {
      text: `${childName}'s afternoon: ${activity.activity_title.toLowerCase()}${lunch}.`,
      dot: 'green'
    };
  }
  if (state === 'picked_up') {
    const parts = [report?.pickup_time ? `picked up at ${report.pickup_time}` : 'picked up'];
    if (report?.mood) parts.push(`${report.mood} mood`);
    return { text: `${childName} — ${parts.join(', ')}.`, dot: 'green' };
  }
  if (state === 'pickup_pending') {
    return { text: `Waiting for ${cName} to confirm pickup.`, dot: 'amber' };
  }
  const planSent = events.some(e =>
    (e.source || '').includes('morning') || (e.source || '').includes('daily_plan')
  );
  if (state === 'in_school' && planSent) {
    return { text: `${childName} is at school. ${cName} has today's plan.`, dot: 'green' };
  }
  if (state === 'in_school') {
    return { text: `${childName} is at school.`, dot: 'green' };
  }
  return { text: `${childName}'s day is ahead.`, dot: 'green' };
}


// ─── Timeline Builder ───────────────────────────────────────────────

function buildTimelineEntries(timeline, dailyPlan, nanny, recentInbox, childName, cName) {
  if (!timeline?.events && !dailyPlan) return [];

  const entries = [];
  const todayStr = new Date().toISOString().slice(0, 10);
  const report = nanny?.report;

  // Process timeline events
  for (const event of (timeline?.events || [])) {
    const source = event.source || '';
    const details = event.details || '';

    if (source.includes('morning_bag') || source === 'daily_plan') {
      entries.push({
        id: event.id, time: event.time_display, type: 'morning_plan',
        title: `${cName} received today's plan`,
        showExplainer: 'plan',
      });
      continue;
    }

    if (source === 'pickup' && event.type === 'execution_log') {
      continue; // handled via daily_plan pickup below
    }

    if (source.includes('checkpoint_pickup') || source === 'lunch_pickup_reminder' || source === 'lunch_pickup') {
      entries.push({
        id: event.id, time: event.time_display, type: 'system',
        title: `Pickup check-in sent to ${cName}`,
      });
      continue;
    }

    if (source === 'activity_suggestion') {
      entries.push({
        id: event.id, time: event.time_display, type: 'system',
        title: `Activity guidance sent to ${cName}`,
        content: details !== 'Activity suggestion sent' ? details : null,
      });
      continue;
    }

    if (source.includes('checkpoint_afternoon')) {
      entries.push({
        id: event.id, time: event.time_display, type: 'system',
        title: `Afternoon check-in sent to ${cName}`,
      });
      continue;
    }

    if (source.includes('checkpoint_evening')) {
      entries.push({
        id: event.id, time: event.time_display, type: 'system',
        title: `Evening story sent to ${cName}`,
      });
      continue;
    }

    if (source.includes('checkpoint_eod') || source.includes('eod')) {
      entries.push({
        id: event.id, time: event.time_display, type: 'system',
        title: 'Daily summary delivered to you',
      });
      continue;
    }

    if (source === 'gmail_processed' || source === 'email_alert' || source === 'gmail_urgent_alert') {
      entries.push({
        id: event.id, time: event.time_display,
        type: event.urgent ? 'alert' : 'email',
        title: event.urgent ? `Urgent: ${details}` : 'School email processed',
        content: !event.urgent ? details : null,
        urgent: event.urgent,
      });
      continue;
    }

    if (source === 'delay') {
      entries.push({
        id: event.id, time: event.time_display, type: 'alert',
        title: event.label || 'Delay reported', content: details,
      });
      continue;
    }

    if (source.includes('health') || source.includes('urgent')) {
      entries.push({
        id: event.id, time: event.time_display, type: 'alert',
        title: event.label || 'Health concern', content: details, urgent: true,
      });
      continue;
    }

    if (source.includes('missed')) {
      entries.push({
        id: event.id, time: event.time_display, type: 'alert',
        title: `${cName} didn't respond to check-in`,
      });
      continue;
    }
  }

  // Pickup from daily_plan
  if (dailyPlan?.pickup_confirmed_at) {
    const pickupTime = dailyPlan.pickup_time_text || formatTimeFromISO(dailyPlan.pickup_confirmed_at);
    // Get raw caregiver text from latest logs
    const latestLog = (nanny?.latest_logs || [])[0];
    const rawText = latestLog?.value_json?.raw_message || latestLog?.value_json?.mood_text;
    entries.push({
      id: 'plan_pickup', time: pickupTime, type: 'pickup',
      title: `Pickup confirmed${report?.mood ? ` · ${capitalizeFirst(report.mood)}` : ''}`,
      content: rawText ? `${cName}: "${rawText}"` : null,
    });
  }

  // Activity from daily_plan checklist
  const checklist = dailyPlan?.activity_checklist || [];
  const completedActivity = checklist.find(a => a.status === 'done');
  const pendingActivity = checklist.find(a => a.status === 'todo');
  const skippedActivity = checklist.find(a => a.status === 'skipped');

  if (completedActivity) {
    entries.push({
      id: `activity_${completedActivity.id}`,
      time: formatTimeFromISO(completedActivity.completed_at) || '4:30 PM',
      type: 'activity', status: 'done',
      title: completedActivity.activity_title || 'Guided Activity',
      description: completedActivity.activity_description || null,
      showExplainer: 'activity',
    });
  } else if (pendingActivity) {
    entries.push({
      id: `activity_p_${pendingActivity.id}`,
      time: pendingActivity.suggested_time || '4:30 PM',
      type: 'activity', status: 'pending',
      title: pendingActivity.activity_title || 'Guided Activity',
      description: pendingActivity.activity_description || null,
    });
  } else if (skippedActivity) {
    entries.push({
      id: `activity_s_${skippedActivity.id}`,
      time: formatTimeFromISO(skippedActivity.completed_at) || '4:30 PM',
      type: 'activity', status: 'skipped',
      title: skippedActivity.activity_title || 'Guided Activity',
      description: skippedActivity.activity_description || null,
    });
  }

  // Today's email-based task extractions
  const todayEmails = (recentInbox || []).filter(e => (e.created_at || '').slice(0, 10) === todayStr);
  for (const email of todayEmails) {
    if (email.tasks_created > 0) {
      const alreadyCovered = entries.some(e => e.type === 'email');
      if (!alreadyCovered) {
        entries.push({
          id: `email_${email.id}`, time: formatTimeFromISO(email.created_at), type: 'email',
          title: `School email: ${(email.subject || '').slice(0, 50)}`,
          content: `→ ${email.tasks_created} task${email.tasks_created > 1 ? 's' : ''} extracted`,
        });
      }
    }
  }

  // Sort by time
  entries.sort((a, b) => parseTimeDisplay(a.time) - parseTimeDisplay(b.time));

  // Deduplicate
  const seen = new Set();
  return entries.filter(e => {
    const key = `${e.type}_${e.title}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}


// ─── Timeline Entry Component ───────────────────────────────────────

const TimelineEntryComponent = ({ entry, photos, planItems, childName, cName }) => {
  const isActivity = entry.type === 'activity';
  const isAlert = entry.type === 'alert';

  const devArea = isActivity ? parseDevelopmentalArea(entry.description) : null;
  const whyLine = isActivity ? parseWhyLine(entry.description) : null;
  const instructionGlimpse = isActivity && entry.status === 'done'
    ? parseInstructionGlimpse(entry.description) : null;

  // One-time explainer
  const [showExplainer, setShowExplainer] = useState(false);
  useEffect(() => {
    if (!entry.showExplainer) return;
    const key = entry.showExplainer === 'plan'
      ? 'kivo_seen_plan_explainer'
      : 'kivo_seen_activity_explainer';
    if (!localStorage.getItem(key)) {
      setShowExplainer(true);
      const timer = setTimeout(() => localStorage.setItem(key, '1'), 5000);
      return () => clearTimeout(timer);
    }
  }, [entry.showExplainer]);

  // Nearby photo
  const entryPhoto = (isActivity || entry.type === 'pickup')
    ? findNearbyPhoto(photos, entry.time) : null;

  return (
    <div className="relative">
      {/* Dot on connecting line */}
      <div className={`absolute -left-6 top-1 w-2 h-2 rounded-full ${
        isAlert ? 'bg-amber-400' :
        isActivity ? 'bg-primary' :
        entry.type === 'pickup' ? 'bg-emerald-400' :
        'bg-slate-200'
      }`} />

      {/* ── Activity Entry — the premium moment ── */}
      {isActivity ? (
        <div className={`rounded-xl px-4 py-4 ${
          entry.status === 'done' ? 'bg-[#F5E6D3]/15' :
          'bg-slate-50/50'
        }`}>
          <p className="text-xs text-slate-400 mb-2">{entry.time}</p>

          <h3 className="font-heading text-base font-semibold text-slate-800 leading-snug">
            {entry.title}
          </h3>

          {(devArea || whyLine) && (
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {devArea && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                  {devArea}
                </span>
              )}
              {whyLine && (
                <span className="text-xs text-slate-500">{whyLine}</span>
              )}
            </div>
          )}

          {instructionGlimpse && (
            <p className="text-sm text-slate-500 italic mt-2">"{instructionGlimpse}"</p>
          )}

          <p className="text-sm text-slate-600 mt-2">
            {entry.status === 'done' && (
              <>Guided for {childName} based on today's energy and age.
              <br /><span className="text-slate-500">{cName} followed the steps — completed.</span></>
            )}
            {entry.status === 'pending' && (
              <span className="text-slate-400">Guided activity — awaiting completion.</span>
            )}
            {entry.status === 'skipped' && (
              <span className="text-slate-400">Skipped today.</span>
            )}
          </p>

          {showExplainer && entry.showExplainer === 'activity' && (
            <div className="border-t border-slate-200/60 mt-3 pt-2">
              <p className="text-xs text-slate-400 italic leading-relaxed">
                Activities are selected across 5 developmental areas — motor, cognitive, creative,
                emotional, physical — matched to your child's age and mood.
                {cName !== 'Your caregiver' ? ` ${cName}` : ' Your caregiver'} receives step-by-step guidance.
              </p>
            </div>
          )}
        </div>

      ) : (
        /* ── Standard entry ── */
        <div>
          <p className="text-xs text-slate-400">{entry.time}</p>
          <p className={`text-sm mt-0.5 ${isAlert ? 'text-amber-700' : 'text-slate-600'}`}>
            {entry.title}
          </p>

          {entry.content && (
            <p className={`text-sm mt-1 ${
              entry.content.startsWith('→') ? 'text-primary font-medium' :
              entry.content.includes('"') ? 'text-slate-500 italic' :
              'text-slate-500'
            }`}>
              {entry.content}
            </p>
          )}

          {/* Morning plan items */}
          {planItems && planItems.length > 0 && (
            <div className="bg-slate-50 rounded-lg px-3 py-2 mt-2">
              <p className="text-sm text-slate-600">{planItems.join(' · ')}</p>
            </div>
          )}

          {showExplainer && entry.showExplainer === 'plan' && (
            <div className="border-t border-slate-200/60 mt-3 pt-2">
              <p className="text-xs text-slate-400 italic leading-relaxed">
                Kivo builds this plan from school emails, your child's schedule,
                and the school calendar. It updates every school morning.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Inline photo */}
      {entryPhoto?.url && (
        <div className="mt-3">
          <img src={entryPhoto.url} alt="Today's moment"
            className="w-full max-h-64 object-cover rounded-xl"
            onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      )}
    </div>
  );
};


// ─── Morning Preview ────────────────────────────────────────────────

const MorningPreview = ({ planItems, cName, childStatus, primaryChild }) => {
  if (childStatus?.state === 'no_school') {
    return (
      <div className="mb-8 px-1">
        <p className="text-sm text-slate-400">No school today. Enjoy the day.</p>
      </div>
    );
  }

  const hasContent = (planItems && planItems.length > 0) || primaryChild?.pickup_time;

  if (!hasContent) {
    return (
      <div className="mb-8 px-1">
        <p className="text-sm text-slate-400 italic">
          The day's story will appear here as it unfolds.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-8 px-1">
      {planItems && planItems.length > 0 && (
        <div className="bg-slate-50 rounded-lg px-4 py-3 mb-3">
          <p className="text-xs text-slate-400 mb-1.5">Today's plan</p>
          <p className="text-sm text-slate-600">{planItems.join(' · ')}</p>
        </div>
      )}
      {primaryChild?.pickup_time && (
        <p className="text-xs text-slate-400">Pickup at {primaryChild.pickup_time}</p>
      )}
      <p className="text-xs text-slate-400 italic mt-3">
        Timeline entries will appear as the day unfolds.
      </p>
    </div>
  );
};


// ─── Helpers ────────────────────────────────────────────────────────

function buildPlanItems(tasks, todayRoutines, primaryChild) {
  const items = [];
  (tasks?.due_today || []).slice(0, 2).forEach(t => items.push(t.title));
  (todayRoutines || []).slice(0, 2).forEach(r => items.push(r.name));
  if (primaryChild?.transport_mode === 'nanny_pickup' && primaryChild?.pickup_time) {
    items.push(`Pickup at ${primaryChild.pickup_time}`);
  }
  return items;
}

function parseDevelopmentalArea(desc) {
  if (!desc) return null;
  const areas = ['Fine Motor', 'Gross Motor', 'Cognitive', 'Creative', 'Emotional', 'Physical'];
  const lower = desc.toLowerCase();
  for (const area of areas) {
    if (lower.includes(area.toLowerCase())) return area;
  }
  return null;
}

function parseWhyLine(desc) {
  if (!desc) return null;
  const dash = desc.match(/[—–-]\s*(.+?)(?:\.|$)/);
  if (dash) return dash[1].trim();
  const paren = desc.match(/\((.+?)\)/);
  if (paren) return paren[1].trim();
  if (desc.length < 80) return desc;
  return null;
}

function parseInstructionGlimpse(desc) {
  if (!desc) return null;
  const steps = desc.match(/(?:1[.)]\s*)(.+?)(?:2[.)]\s*(.+?))?(?:3[.)]|$)/);
  if (steps) {
    const parts = [steps[1]?.trim(), steps[2]?.trim()].filter(Boolean);
    if (parts.length > 0) return parts.join(', ');
  }
  return null;
}

function formatTimeFromISO(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).replace(/\s/, ' ');
  } catch { return null; }
}

function parseTimeDisplay(timeStr) {
  if (!timeStr) return 0;
  const m = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 0;
  let h = parseInt(m[1]);
  const min = parseInt(m[2]);
  if (m[3].toUpperCase() === 'PM' && h !== 12) h += 12;
  if (m[3].toUpperCase() === 'AM' && h === 12) h = 0;
  return h * 60 + min;
}

function findNearbyPhoto(photos, timeStr) {
  if (!photos?.length || !timeStr) return null;
  const entryMins = parseTimeDisplay(timeStr);
  for (const photo of photos) {
    if (!photo.taken_at) continue;
    const d = new Date(photo.taken_at);
    const photoMins = d.getHours() * 60 + d.getMinutes();
    if (Math.abs(photoMins - entryMins) < 30) return photo;
  }
  return null;
}

function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}


export default ParentToday;
